/* global HTMLDivElement, KeyboardEvent */
/**
 * confirm-dialog.tsx — thay thế window.confirm/window.prompt bằng modal theo
 * theme bento (native dialog của browser phá theme + prompt block JS thread).
 * Style bám theo modal "Xoá mục này?" trong bento-shell.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { useFocusTrap } from "../lib/focus-trap";

function DialogShell({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-label={label}>
      <div ref={panelRef} className="fade-in w-full max-w-sm rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/** Modal xác nhận hành động — thay window.confirm. Mount có điều kiện: `{open && <ConfirmDialog …/>}`. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Tiếp tục",
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <DialogShell label={title} onClose={onClose}>
      <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-amber-50 text-amber-500 dark:bg-amber-500/10 dark:text-amber-300">
        <AlertTriangle className="h-7 w-7" />
      </div>
      <h3 className="text-center font-display text-[20px] font-bold">{title}</h3>
      <p className="mt-1 text-center text-[13px] text-ink-500">{message}</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <button onClick={onClose} className="rounded-xl bg-ink-200/60 py-3 text-[14px] font-bold text-ink-700">Huỷ</button>
        <button
          onClick={() => { onConfirm(); onClose(); }}
          className="rounded-xl bg-rose-500 py-3 text-[14px] font-bold text-white hover:bg-rose-600 transition"
        >
          {confirmLabel}
        </button>
      </div>
    </DialogShell>
  );
}

/** Modal nhập text ngắn — thay window.prompt. Submit bị chặn khi rỗng,
 *  trừ khi `allowEmpty` (lý do tuỳ chọn — vd nới MIT replan). */
export function PromptDialog({
  title,
  message,
  placeholder = "",
  submitLabel = "Xác nhận",
  allowEmpty = false,
  presets,
  onSubmit,
  onClose,
}: {
  title: string;
  message?: string;
  placeholder?: string;
  submitLabel?: string;
  allowEmpty?: boolean;
  /** Optional quick-pick chips — clicking one submits that value immediately. */
  presets?: string[];
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const canSubmit = allowEmpty || value.trim().length > 0;
  const submit = () => {
    if (!canSubmit) return;
    onSubmit(value.trim());
    onClose();
  };
  return (
    <DialogShell label={title} onClose={onClose}>
      <h3 className="text-center font-display text-[18px] font-bold">{title}</h3>
      {message && <p className="mt-1 text-center text-[13px] text-ink-500">{message}</p>}
      {presets && presets.length > 0 && (
        <div className="mt-3 flex flex-wrap justify-center gap-1.5">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => { onSubmit(p); onClose(); }}
              className="rounded-full bg-ink-100 px-3 py-1.5 text-[12px] font-semibold text-ink-700 transition hover:bg-brand-100 hover:text-brand-600"
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <input
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        className="mt-4 w-full rounded-xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[14px] outline-none focus:border-brand-400 focus:bg-white"
      />
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button onClick={onClose} className="rounded-xl bg-ink-200/60 py-3 text-[14px] font-bold text-ink-700">Huỷ</button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="rounded-xl gbrand py-3 text-[14px] font-bold text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </DialogShell>
  );
}
